import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Collapse,
  Grid,
  Group,
  Loader,
  MultiSelect,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconBuildingStore,
  IconClock,
  IconCoin,
  IconCoins,
  IconFilter,
  IconFilterOff,
  IconTrendingDown,
  IconTrendingUp,
  IconWallet,
} from '@tabler/icons-react';
import { walletApi } from '../api/wallet';
import { useProfile, usePermissions } from '../auth/SessionContext';
import { isMerchantUser, isPlatform } from '../auth/permissions';
import { formatGmt7, formatIdr } from '../lib/format';
import { ApiCallError } from '../api/client';
import { useTranslation } from '../i18n/LocaleContext';
import { DataTable } from '../components/DataTable';
import type { TranslationKey } from '../i18n/translations';
import type { InternalWalletLedgerFilter, WalletMutationCategory } from '../api/types';

// Balance Mutation only surfaces settlement, withdrawal, and balance
// adjustments — map the raw ledger trxType onto one of those categories so the
// UI reads as a mutation log rather than exposing internal trx-type strings.
type MutCategory = 'settlement' | 'withdrawal' | 'adjustment' | 'fee' | 'other';
function mutationCategory(trxType: string): MutCategory {
  if (trxType === 'deposit_settled') return 'settlement';
  if (trxType.startsWith('disbursement_')) return 'withdrawal';
  if (trxType === 'manual_adjustment') return 'adjustment';
  if (trxType === 'fee_collected') return 'fee';
  return 'other';
}
const MUT_COLOR: Record<MutCategory, string> = {
  settlement: 'green',
  withdrawal: 'grape',
  adjustment: 'orange',
  fee: 'blue',
  other: 'gray',
};
const MUT_LABEL_KEY: Record<MutCategory, TranslationKey> = {
  settlement: 'wallet.mutSettlement',
  withdrawal: 'wallet.mutWithdrawal',
  adjustment: 'wallet.mutAdjustment',
  fee: 'wallet.mutFee',
  other: 'wallet.mutOther',
};

function signedAmount(n: number): string {
  if (n === 0) return '—';
  return `${n > 0 ? '+' : ''}${formatIdr(n)}`;
}

// Pending filter state for the internal-wallet Balance-Mutation table. Arrays
// are multi-select; trxId is free text. Applied to the query on "Apply".
type WalletFilterDraft = {
  trxId: string;
  merchantId: string[];
  type: WalletMutationCategory[];
  createdBy: string[];
};
const EMPTY_WALLET_DRAFT: WalletFilterDraft = {
  trxId: '',
  merchantId: [],
  type: [],
  createdBy: [],
};
// Type-filter options mirror the mutation-category badges shown in the table.
const TYPE_FILTER_OPTIONS: { value: WalletMutationCategory; labelKey: TranslationKey }[] = [
  { value: 'settlement', labelKey: 'wallet.mutSettlement' },
  { value: 'withdrawal', labelKey: 'wallet.mutWithdrawal' },
  { value: 'adjustment', labelKey: 'wallet.mutAdjustment' },
  { value: 'fee', labelKey: 'wallet.mutFee' },
];

/**
 * Scope-aware wallet page:
 *  - merchant (external) users see their OWN wallet + ledger.
 *  - internal users / superusers see the platform-wide AGGREGATE across every
 *    merchant (gated by `wallet.internal.view`).
 */
export function WalletPage() {
  const profile = useProfile();
  const { t } = useTranslation();
  if (isMerchantUser(profile) && profile.merchantId != null) {
    return <MerchantWalletView merchantId={profile.merchantId} />;
  }
  if (isPlatform(profile)) {
    return <InternalWalletView />;
  }
  return (
    <Alert color="gray" icon={<IconAlertCircle size={16} />}>
      {t('wallet.merchantOnlyHint')}
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Merchant (own wallet) view — unchanged behavior.
// ---------------------------------------------------------------------------
function MerchantWalletView({ merchantId }: { merchantId: string }) {
  const { t } = useTranslation();
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [pageIdx, setPageIdx] = useState(0);

  const walletQ = useQuery({
    queryKey: ['wallet', merchantId],
    queryFn: () => walletApi.get(merchantId),
    refetchInterval: 5000,
    retry: 0,
  });

  const ledgerQ = useQuery({
    queryKey: ['wallet-ledger', merchantId, cursorStack[pageIdx]],
    queryFn: () => walletApi.ledger(merchantId, { cursor: cursorStack[pageIdx] ?? undefined, limit: 25 }),
    retry: 0,
    placeholderData: (prev) => prev,
  });

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>{t('wallet.title')}</Title>
          <Text c="dimmed" size="sm">
            {t('wallet.subtitle')}
          </Text>
        </div>
        <Badge variant="light">{t('wallet.merchantIdLabel', { id: merchantId })}</Badge>
      </Group>

      {walletQ.isLoading && (
        <Group justify="center" py="lg">
          <Loader />
        </Group>
      )}
      {walletQ.error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {walletQ.error instanceof ApiCallError
            ? `${walletQ.error.code}: ${walletQ.error.message}`
            : t('common.error')}
        </Alert>
      )}
      {walletQ.data && (
        <>
          <Alert color="blue" variant="light" icon={<IconClock size={16} />}>
            <Text size="sm" fw={600}>{t('wallet.settlementTimingTitle')}</Text>
            <Text size="xs" mt={4}>
              {t('wallet.settlementTimingIntro')}
            </Text>
            <Text size="xs" mt={2}>
              • {t('wallet.settlementBatchMorning')}<br />
              • {t('wallet.settlementBatchEvening')}<br />
              • {t('wallet.settlementBatchException')}
            </Text>
            <Text size="xs" mt={4}>
              {t('wallet.settlementMeaning')}
            </Text>
          </Alert>
          <Grid>
            <BalanceCard labelKey="wallet.balPending" value={walletQ.data.pendingBalance} icon={IconClock} color="yellow" hintKey="wallet.balPendingHint" />
            <BalanceCard labelKey="wallet.balActive" value={walletQ.data.activeBalance} icon={IconWallet} color="green" hintKey="wallet.balActiveHint" />
            <BalanceCard labelKey="wallet.balReserved" value={walletQ.data.reservedBalance} icon={IconCoins} color="orange" hintKey="wallet.balReservedHint" />
            <BalanceCard labelKey="wallet.balLifetimeDeposit" value={walletQ.data.lifetimeDeposit} icon={IconTrendingUp} color="blue" />
            <BalanceCard labelKey="wallet.balLifetimeDisbursement" value={walletQ.data.lifetimeDisbursement} icon={IconTrendingDown} color="grape" />
            <BalanceCard labelKey="wallet.balLifetimeFee" value={walletQ.data.lifetimeFee} icon={IconCoin} color="teal" />
          </Grid>
        </>
      )}

      <LedgerSection
        loading={ledgerQ.isLoading}
        rows={ledgerQ.data?.rows ?? []}
        hasMore={ledgerQ.data?.hasMore ?? false}
        pageIdx={pageIdx}
        onPrev={() => setPageIdx((i) => i - 1)}
        onNext={() => {
          if (ledgerQ.data?.nextCursor) {
            setCursorStack((s) => [...s.slice(0, pageIdx + 1), ledgerQ.data!.nextCursor]);
            setPageIdx((i) => i + 1);
          }
        }}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Internal (all-merchant aggregate) view.
// ---------------------------------------------------------------------------
function InternalWalletView() {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [pageIdx, setPageIdx] = useState(0);

  // Gate: superuser bypasses (has() returns true); internal users need the perm.
  // ALL hooks must run unconditionally (rules of hooks) — `has()` can flip from
  // false→true as permissions hydrate, so the early return lives BELOW the
  // hooks. The queries are `enabled` only when allowed, so a forbidden viewer
  // never fires a doomed 403 request.
  const allowed = has('wallet.internal.view');

  // Filter: a pending `draft` (edited by the controls) applied to `appliedFilter`
  // on "Apply" — same pattern as the Deposits page.
  const [draft, setDraft] = useState<WalletFilterDraft>(EMPTY_WALLET_DRAFT);
  const [appliedFilter, setAppliedFilter] = useState<InternalWalletLedgerFilter>({});
  const [filtersOpen, setFiltersOpen] = useState(true);

  const summaryQ = useQuery({
    queryKey: ['wallet-internal-summary'],
    queryFn: () => walletApi.internalSummary(),
    refetchInterval: 5000,
    retry: 0,
    enabled: allowed,
  });

  // Dropdown option lists (merchants + createdBy) — self-contained endpoint.
  const optionsQ = useQuery({
    queryKey: ['wallet-internal-ledger-filters'],
    queryFn: () => walletApi.internalLedgerFilters(),
    retry: 0,
    enabled: allowed,
  });

  const ledgerQ = useQuery({
    queryKey: ['wallet-internal-ledger', appliedFilter, cursorStack[pageIdx]],
    queryFn: () =>
      walletApi.internalLedger({
        ...appliedFilter,
        cursor: cursorStack[pageIdx] ?? undefined,
        limit: 25,
      }),
    retry: 0,
    placeholderData: (prev) => prev,
    enabled: allowed,
  });

  const applyFilters = () => {
    setCursorStack([null]);
    setPageIdx(0);
    const f: InternalWalletLedgerFilter = {};
    if (draft.trxId.trim()) f.trxId = draft.trxId.trim();
    if (draft.merchantId.length) f.merchantId = draft.merchantId;
    if (draft.type.length) f.type = draft.type;
    if (draft.createdBy.length) f.createdBy = draft.createdBy;
    setAppliedFilter(f);
  };
  const clearFilters = () => {
    setCursorStack([null]);
    setPageIdx(0);
    setDraft(EMPTY_WALLET_DRAFT);
    setAppliedFilter({});
  };
  const activeCount =
    (draft.trxId.trim() ? 1 : 0) +
    (draft.merchantId.length ? 1 : 0) +
    (draft.type.length ? 1 : 0) +
    (draft.createdBy.length ? 1 : 0);

  if (!allowed) {
    return (
      <Stack gap="md">
        <Title order={2}>{t('wallet.internalTitle')}</Title>
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {t('wallet.internalAccessDenied')}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>{t('wallet.internalTitle')}</Title>
          <Text c="dimmed" size="sm">
            {t('wallet.internalSubtitle')}
          </Text>
        </div>
        {summaryQ.data && (
          <Badge variant="light" leftSection={<IconBuildingStore size={12} />}>
            {t('wallet.internalMerchantCount', { count: summaryQ.data.merchantCount })}
          </Badge>
        )}
      </Group>

      {summaryQ.isLoading && (
        <Group justify="center" py="lg">
          <Loader />
        </Group>
      )}
      {summaryQ.error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {summaryQ.error instanceof ApiCallError
            ? `${summaryQ.error.code}: ${summaryQ.error.message}`
            : t('common.error')}
        </Alert>
      )}
      {summaryQ.data && (
        <Grid>
          <BalanceCard labelKey="wallet.balPending" value={summaryQ.data.pendingBalance} icon={IconClock} color="yellow" />
          <BalanceCard labelKey="wallet.balActive" value={summaryQ.data.activeBalance} icon={IconWallet} color="green" />
          <BalanceCard labelKey="wallet.balReserved" value={summaryQ.data.reservedBalance} icon={IconCoins} color="orange" />
          <BalanceCard labelKey="wallet.balLifetimeDeposit" value={summaryQ.data.lifetimeDeposit} icon={IconTrendingUp} color="blue" />
          <BalanceCard labelKey="wallet.balLifetimeDisbursement" value={summaryQ.data.lifetimeDisbursement} icon={IconTrendingDown} color="grape" />
          <BalanceCard labelKey="wallet.balLifetimeFee" value={summaryQ.data.lifetimeFee} icon={IconCoin} color="teal" />
        </Grid>
      )}

      {/* ---- Filters (Balance Mutation) ---- */}
      <Card>
        <Group justify="space-between" mb={filtersOpen ? 'md' : 0}>
          <Group gap="xs">
            <Text size="sm" fw={600}>{t('wallet.filters')}</Text>
            {activeCount > 0 && (
              <Badge size="sm" variant="light">{t('wallet.activeFilters', { n: activeCount })}</Badge>
            )}
          </Group>
          <Button
            size="xs"
            variant="subtle"
            leftSection={filtersOpen ? <IconFilterOff size={14} /> : <IconFilter size={14} />}
            onClick={() => setFiltersOpen((o) => !o)}
          >
            {filtersOpen ? t('common.collapse') : t('common.show')}
          </Button>
        </Group>
        <Collapse in={filtersOpen}>
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <MultiSelect
                label={t('wallet.filterMerchant')}
                // Mantine passes `placeholder` straight to the inner search
                // input, which keeps showing it next to the pills; clear it
                // once anything is selected.
                placeholder={draft.merchantId.length ? '' : t('wallet.filterAnyMerchant')}
                searchable
                clearable
                data={(optionsQ.data?.merchants ?? []).map((m) => ({ value: m.id, label: m.name }))}
                value={draft.merchantId}
                onChange={(v) => setDraft((d) => ({ ...d, merchantId: v }))}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <MultiSelect
                label={t('wallet.filterType')}
                placeholder={draft.type.length ? '' : t('wallet.filterAnyType')}
                clearable
                data={TYPE_FILTER_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
                value={draft.type}
                onChange={(v) => setDraft((d) => ({ ...d, type: v as WalletMutationCategory[] }))}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput
                label={t('wallet.filterTrxId')}
                placeholder={t('wallet.filterTrxIdPlaceholder')}
                value={draft.trxId}
                onChange={(e) => setDraft((d) => ({ ...d, trxId: e.currentTarget.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <MultiSelect
                label={t('wallet.filterCreatedBy')}
                placeholder={draft.createdBy.length ? '' : t('wallet.filterAnyCreatedBy')}
                searchable
                clearable
                data={(optionsQ.data?.createdBy ?? []).map((c) => ({ value: c, label: c }))}
                value={draft.createdBy}
                onChange={(v) => setDraft((d) => ({ ...d, createdBy: v }))}
              />
            </Grid.Col>
          </Grid>
          <Group justify="flex-end" mt="md" gap="xs">
            <Button variant="default" size="xs" onClick={clearFilters} disabled={activeCount === 0 && Object.keys(appliedFilter).length === 0}>
              {t('wallet.filterClear')}
            </Button>
            <Button size="xs" onClick={applyFilters}>{t('wallet.filterApply')}</Button>
          </Group>
        </Collapse>
      </Card>

      <LedgerSection
        loading={ledgerQ.isLoading}
        rows={ledgerQ.data?.rows ?? []}
        hasMore={ledgerQ.data?.hasMore ?? false}
        pageIdx={pageIdx}
        showMerchant
        onPrev={() => setPageIdx((i) => i - 1)}
        onNext={() => {
          if (ledgerQ.data?.nextCursor) {
            setCursorStack((s) => [...s.slice(0, pageIdx + 1), ledgerQ.data!.nextCursor]);
            setPageIdx((i) => i + 1);
          }
        }}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Shared balance-mutation (ledger) table. `showMerchant` adds the Merchant
// column for the internal cross-merchant view; rows there carry `merchantName`.
// ---------------------------------------------------------------------------
type LedgerRow = {
  id: number;
  trxId: string | null;
  trxType: string;
  amountPending: number;
  amountActive: number;
  amountReserved: number;
  createdBy: string;
  createdAt: string;
  merchantName?: string;
};
function LedgerSection({
  loading,
  rows,
  hasMore,
  pageIdx,
  onPrev,
  onNext,
  showMerchant = false,
}: {
  loading: boolean;
  rows: LedgerRow[];
  hasMore: boolean;
  pageIdx: number;
  onPrev: () => void;
  onNext: () => void;
  showMerchant?: boolean;
}) {
  const { t } = useTranslation();
  const colSpan = showMerchant ? 8 : 7;
  return (
    <>
      <div>
        <Title order={3}>{t('wallet.ledgerTitle')}</Title>
        <Text c="dimmed" size="sm">
          {t('wallet.ledgerSubtitle')}
        </Text>
      </div>
      <Card p={0}>
        {loading ? (
          <Group justify="center" py="lg">
            <Loader />
          </Group>
        ) : (
          <DataTable
            tableId={showMerchant ? 'wallet-ledger-all' : 'wallet-ledger'}
            columns={[
              ...(showMerchant
                ? [{ key: 'merchant', label: t('wallet.ledgerColMerchant'), width: 200 }]
                : []),
              { key: 'type', label: t('wallet.ledgerColType'), width: 170 },
              { key: 'trxId', label: t('wallet.ledgerColTrxId'), width: 180 },
              { key: 'pending', label: t('wallet.ledgerColPending'), width: 150, align: 'right' },
              { key: 'active', label: t('wallet.ledgerColActive'), width: 150, align: 'right' },
              { key: 'reserved', label: t('wallet.ledgerColReserved'), width: 150, align: 'right' },
              { key: 'createdBy', label: t('wallet.ledgerColCreatedBy'), width: 220 },
              { key: 'created', label: t('wallet.ledgerColCreated'), width: 170 },
            ]}
          >
                {rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={colSpan}>
                      <Text c="dimmed" ta="center" py="lg">
                        {t('wallet.ledgerEmpty')}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {rows.map((row) => (
                  <Table.Tr key={row.id}>
                    {showMerchant && (
                      <Table.Td>
                        <Text size="sm" fw={500}>{row.merchantName ?? '—'}</Text>
                      </Table.Td>
                    )}
                    <Table.Td>
                      <Badge color={MUT_COLOR[mutationCategory(row.trxType)]} variant="light">
                        {t(MUT_LABEL_KEY[mutationCategory(row.trxType)])}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Code style={{ fontSize: 11 }}>{row.trxId ?? '—'}</Code>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{signedAmount(row.amountPending)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{signedAmount(row.amountActive)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{signedAmount(row.amountReserved)}</Table.Td>
                    <Table.Td>{row.createdBy}</Table.Td>
                    <Table.Td>{formatGmt7(row.createdAt)}</Table.Td>
                  </Table.Tr>
                ))}
          </DataTable>
        )}
        <Group justify="space-between" p="md">
          <Text c="dimmed" size="xs">
            {t('wallet.pageRows', { page: pageIdx + 1, count: rows.length })}
          </Text>
          <Group gap="xs">
            <Button variant="default" size="xs" disabled={pageIdx === 0} onClick={onPrev}>
              {t('common.prev')}
            </Button>
            <Button variant="default" size="xs" disabled={!hasMore} onClick={onNext}>
              {t('common.next')}
            </Button>
          </Group>
        </Group>
      </Card>
    </>
  );
}

function BalanceCard({
  labelKey,
  value,
  icon: Icon,
  color,
  hintKey,
}: {
  labelKey: TranslationKey;
  value: number;
  icon: typeof IconClock;
  color: string;
  hintKey?: TranslationKey;
}) {
  const { t } = useTranslation();
  return (
    <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
      <Card h="100%">
        {/* nowrap keeps the icon pinned top-right even when the hint wraps to
            several lines; without it Group wraps the icon below the text and
            the card grows lopsided at narrow widths. */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Text c="dimmed" size="xs" tt="uppercase" fw={600}>
              {t(labelKey)}
            </Text>
            <Text size="xl" fw={700}>
              {formatIdr(value)}
            </Text>
            {hintKey && (
              <Text c="dimmed" size="xs">
                {t(hintKey)}
              </Text>
            )}
          </Stack>
          <ThemeIcon color={color} variant="light" size={40} radius="md" style={{ flexShrink: 0 }}>
            <Icon size={22} />
          </ThemeIcon>
        </Group>
      </Card>
    </Grid.Col>
  );
}
